import React, { useState, useRef, useEffect, useCallback, useLayoutEffect, useMemo, useImperativeHandle } from 'react';
import { useRubberBand } from '../../../hooks/useRubberBand';
import { Task, Submission, ThreadMessage } from '../../../types';
import { TaskCard, TaskCardState } from './TaskCard';
import { GlassCardStack } from './GlassCardStack';
import { MiniTaskCard } from './MiniTaskCard';
import { SmartActionBar } from './SmartActionBar';
import { SubmissionCard } from '../feed/SubmissionCard';
import { AUDIO } from '../../../utils/audioFeedback';
import { useUploadManager } from '../../../contexts/UploadManagerContext';
import { HelpOverlay, shouldShowHelp } from './HelpOverlay';
import './TaskList.css';
import './TaskList.glass.css';

/** Per-task state provided by the parent (who knows drafts, uploads, etc.) */
export interface TaskStateEntry {
  taskId: string;
  state: TaskCardState;
  /** Redo reason from researcher — shown on needs_redo cards */
  redoReason?: string;
  /** Parent submission ID for redo lineage */
  redoParentSubmissionId?: string;
}

export interface TaskListProps {
  tasks: Task[];
  /** Map of taskId → card state. Tasks not listed default to 'active'. */
  taskStates?: TaskStateEntry[];
  activeTaskId?: string;
  /** Select a task (show card) — does NOT start capture */
  onSelectTask?: (task: Task) => void;
  /** Start a task (enter capture flow) */
  onStartTask?: (task: Task) => void;
  onResumeTask?: (task: Task) => void;
  /** Task whose draft is currently being opened — shows a loading state and
      blocks repeat taps while a remote draft's media downloads. */
  resumingTaskId?: string | null;
  onOpenNavigator?: () => void;
  /** Submissions data for inline drawer */
  submissions?: Submission[];
  /** All thread messages grouped by submissionId */
  threadsBySubmission?: Map<string, ThreadMessage[]>;
  /** Unread researcher message counts per submissionId */
  unreadCounts?: Map<string, number>;
  /** Thread authoring context for replies */
  threadContext?: {
    currentUserUid: string;
    currentUserName: string;
    organizationId: string;
  };
  /** Called when participant taps "Add more" on a submission */
  onAddendum?: (submission: Submission, task: Task) => void;
  /** Fired when the just-completed card's timed sequence finishes */
  onSubmitSequenceDone?: () => void;
  /** Milestone message to show on the just-completed card */
  milestone?: string;
  /** Submission receipt timestamp for the just-completed card */
  submittedAt?: Date;
  /** Fired when participant accepts a redo request */
  onRedoTask?: (task: Task) => void;
  /** Total unread thread messages across all submissions */
  unreadMessageCount?: number;
  /** Number of tasks with pending redo requests */
  redoCount?: number;
  /** Opens conversations inbox (future — falls back to feed for now) */
  onViewMessages?: () => void;
  /** Scroll to a specific submission in the drawer (from inbox navigation) */
  scrollToSubmissionId?: string | null;
  /** Current layout — carousel (default) or 2-up grid */
  viewMode?: 'carousel' | 'grid';
  /** Toggle between carousel ↔ grid */
  onToggleViewMode?: () => void;
}

export interface TaskListHandle {
  /** Snapshot card positions before a mode switch (call before onToggleViewMode) */
  captureRects: () => void;
}

/* ── Time-left helper ── */
function formatTimeLeft(dueDate: string): string {
  const ms = new Date(dueDate).getTime() - Date.now();
  if (ms <= 0) return 'Past due';
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  if (days > 0) return `${days}d left`;
  if (hours > 0) return `${hours}h left`;
  const mins = Math.floor((ms % 3600000) / 60000);
  return mins > 0 ? `${mins}m left` : '< 1m left';
}

/* ── Addendum window helper ── */
function getAddendumTimeLeft(submission: Submission, task: Task | undefined): string | null {
  if (!task?.allowAddendum || !task.addendumWindowHours) return null;
  if (submission.submissionType && submission.submissionType !== 'original') return null;
  const created = submission.createdAt?.toDate
    ? submission.createdAt.toDate()
    : new Date(submission.timestamp || submission.createdAt || 0);
  if (isNaN(created.getTime())) return null;
  const windowMs = task.addendumWindowHours * 3600000;
  const remaining = (created.getTime() + windowMs) - Date.now();
  if (remaining <= 0) return null;
  const hours = Math.floor(remaining / 3600000);
  if (hours >= 24) return `${Math.floor(hours / 24)}d left`;
  if (hours >= 1) return `${hours}h left`;
  const mins = Math.floor(remaining / 60000);
  return `${mins}m left`;
}

export const TaskList = React.forwardRef<TaskListHandle, TaskListProps>(({
  tasks,
  taskStates = [],
  activeTaskId,
  onSelectTask,
  onStartTask,
  onResumeTask,
  resumingTaskId,
  onOpenNavigator,
  submissions = [],
  threadsBySubmission,
  unreadCounts,
  threadContext,
  onAddendum,
  onSubmitSequenceDone,
  milestone,
  submittedAt,
  onRedoTask,
  unreadMessageCount = 0,
  redoCount = 0,
  onViewMessages,
  scrollToSubmissionId,
  viewMode = 'carousel',
  onToggleViewMode,
}, ref) => {
  const helpUserId = threadContext?.currentUserUid;
  const [showHelp, setShowHelp] = useState(() => shouldShowHelp(helpUserId));
  // Re-evaluate once the user id hydrates — first mount can happen before
  // threadContext resolves, which would briefly use the 'anonymous' key and
  // then miss the real per-user flag.
  useEffect(() => {
    if (helpUserId) setShowHelp(shouldShowHelp(helpUserId));
  }, [helpUserId]);
  const [feedExpanded, setFeedExpanded] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  // Open drawer and scroll to a specific submission (from inbox navigation)
  useEffect(() => {
    if (!scrollToSubmissionId) return;
    setFeedExpanded(true);
    // Wait for drawer to open and render
    setTimeout(() => {
      const target = document.querySelector(`[data-submission-id="${scrollToSubmissionId}"]`) as HTMLElement;
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Flash highlight
        target.style.outline = '2px solid var(--ember, #D4715A)';
        target.style.outlineOffset = '2px';
        target.style.borderRadius = '12px';
        setTimeout(() => {
          target.style.outline = '';
          target.style.outlineOffset = '';
        }, 2000);
      }
    }, 400);
  }, [scrollToSubmissionId]);
  const contentRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [visibleTaskId, setVisibleTaskId] = useState<string | null>(null);
  const stateMap = new Map(taskStates.map(e => [e.taskId, e]));

  const getState = (task: Task): TaskCardState => {
    const entry = stateMap.get(task.id);
    if (entry) return entry.state;
    if (task.isUrgent) return 'urgent';
    return 'active';
  };

  const getEntry = (taskId: string) => stateMap.get(taskId);

  // 'just-completed' represents the brief celebration window after a
   // submit lands — the task IS submitted, only the celebration card
   // sequence is still animating. Count it as completed for the dock
   // (progress ring + "remaining") so the bar updates the moment the
   // submission is in, not after the animation finishes. Mirrors the
   // exclusion in incompleteTasks below so the same task never shows
   // up in both lists during that window.
  const completedTasks = tasks.filter(t => {
    const s = getState(t);
    return s === 'complete' || s === 'just-completed';
  });
  const incompleteTasks = tasks.filter(t => {
    const s = getState(t);
    return s !== 'complete' && s !== 'just-completed';
  });

  // Sorted submissions for inline drawer (newest first)
  const taskMap = useMemo(() => new Map(tasks.map(t => [t.id, t])), [tasks]);
  const sortedSubmissions = useMemo(() =>
    [...submissions].sort((a, b) => {
      const ta = new Date(a.timestamp || a.createdAt || 0).getTime();
      const tb = new Date(b.timestamp || b.createdAt || 0).getTime();
      return tb - ta;
    }),
    [submissions],
  );

  // Active uploads — show as optimistic cards at top of drawer
  const { jobs } = useUploadManager();
  const pendingJobs = useMemo(
    () => jobs.filter(j => j.status !== 'done' && j.status !== 'failed'),
    [jobs],
  );

  // Grid shows all tasks: incomplete first, then completed
  const gridTasks = useMemo(
    () => [...incompleteTasks, ...completedTasks],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tasks.length, taskStates],
  );

  // Seed visibleTaskId with first incomplete task when it's never been set.
  // IntersectionObserver doesn't always fire for already-visible elements on mount.
  useEffect(() => {
    if (visibleTaskId === null && incompleteTasks.length > 0 && viewMode === 'carousel') {
      setVisibleTaskId(incompleteTasks[0].id);
    }
  }, [incompleteTasks.length, viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // The currently centered card — driven by scroll position
  const centeredTask = visibleTaskId
    ? incompleteTasks.find(t => t.id === visibleTaskId) || incompleteTasks[0]
    : incompleteTasks[0];

  /* ══════════════════════════════════════════════════════════
     FLIP ANIMATION — smooth position/size morph between layouts
     ══════════════════════════════════════════════════════════ */
  const prevRectsRef = useRef<Map<string, DOMRect>>(new Map());
  const prevViewModeRef = useRef(viewMode);

  /** Snapshot every card's bounding rect — call just before mode switch */
  const captureRects = useCallback(() => {
    const rects = new Map<string, DOMRect>();
    itemRefs.current.forEach((el, taskId) => {
      rects.set(taskId, el.getBoundingClientRect());
    });
    prevRectsRef.current = rects;
  }, []);

  useImperativeHandle(ref, () => ({ captureRects }), [captureRects]);

  /** After the DOM commits the new layout, apply inverse transforms then animate */
  useLayoutEffect(() => {
    const modeChanged = prevViewModeRef.current !== viewMode;
    prevViewModeRef.current = viewMode;
    if (!modeChanged) return;

    // When switching to carousel, sync visibleTaskId and position the active card
    if (viewMode === 'carousel' && carouselRef.current) {
      const targetId = activeTaskId || incompleteTasks[0]?.id;
      if (targetId) setVisibleTaskId(targetId);
      const container = carouselRef.current;
      if (activeTaskId) {
        container.style.scrollSnapType = 'none';
        container.style.scrollBehavior = 'auto';
        const target = itemRefs.current.get(activeTaskId);
        if (target) {
          container.scrollLeft = target.offsetLeft - (container.clientWidth - target.clientWidth) / 2;
        }
        // Re-enable snap after the FLIP animation completes
        requestAnimationFrame(() => {
          setTimeout(() => {
            container.style.scrollSnapType = '';
            container.style.scrollBehavior = '';
          }, 380);
        });
      }
    }

    const oldRects = prevRectsRef.current;
    prevRectsRef.current = new Map();
    if (oldRects.size === 0) return;

    // Stack-mode CSS class transforms (must match GlassCardStack.css). FLIP for
    // stack cards animates to these target values (with matching transform-
    // origin) so the card lands directly at its peek position — no secondary
    // animation when cleanup clears the inline transform.
    const STACK_POS_TRANSFORMS: Record<string, string> = {
      'gcs-pos-0': 'none',
      'gcs-pos-1': 'rotate(5deg) translate(28px, 16px)',
      'gcs-pos-2': 'rotate(9deg) translate(48px, 30px) scale(0.97)',
      'gcs-pos-3': 'rotate(13deg) translate(64px, 44px) scale(0.94)',
    };

    itemRefs.current.forEach((el, taskId) => {
      const oldRect = oldRects.get(taskId);

      if (!oldRect) {
        // Card had no previous position — fade in
        el.style.opacity = '0';
        el.style.transform = 'scale(0.85)';
        requestAnimationFrame(() => {
          el.style.transition = 'opacity 250ms ease, transform 250ms ease';
          el.style.opacity = '1';
          el.style.transform = '';
          el.addEventListener('transitionend', function handler() {
            el.style.transition = '';
            el.removeEventListener('transitionend', handler);
          });
        });
        return;
      }

      // Detect stack card and look up its CSS class target transform.
      let stackTarget: string | null = null;
      for (const cls of Object.keys(STACK_POS_TRANSFORMS)) {
        if (el.classList.contains(cls)) { stackTarget = STACK_POS_TRANSFORMS[cls]; break; }
      }
      const isStackCard = stackTarget !== null;

      // Read NATURAL (un-transformed) rect via offset properties — these
      // ignore CSS transforms, unlike getBoundingClientRect. Avoids the
      // need to temporarily override transform.
      let naturalLeft: number;
      let naturalTop: number;
      let naturalWidth: number;
      let naturalHeight: number;
      if (isStackCard && el.offsetParent) {
        const parentRect = (el.offsetParent as HTMLElement).getBoundingClientRect();
        naturalLeft = parentRect.left + el.offsetLeft;
        naturalTop = parentRect.top + el.offsetTop;
        naturalWidth = el.offsetWidth;
        naturalHeight = el.offsetHeight;
      } else {
        const r = el.getBoundingClientRect();
        naturalLeft = r.left;
        naturalTop = r.top;
        naturalWidth = r.width;
        naturalHeight = r.height;
      }

      // Skip cards that were off-screen — they just appear
      const wasVisible = oldRect.bottom > 0 && oldRect.top < window.innerHeight;
      if (!wasVisible) return;

      const s = naturalWidth > 0 ? oldRect.width / naturalWidth : 1;
      const dx = oldRect.left - naturalLeft;
      const dy = isStackCard
        ? oldRect.top - naturalTop - naturalHeight * (1 - s)
        : oldRect.top - naturalTop;

      const fromTransform = `translate(${dx}px, ${dy}px) scale(${s})`;
      const toTransform = stackTarget ?? 'none';
      const origin = isStackCard ? '0% 100%' : 'top left';

      // Web Animations API — runs in compositor without touching the cascade.
      // After animation completes, element returns to its CSS class state
      // (which is the same as toTransform for stack cards), no extra cleanup.
      el.style.overflow = 'hidden';
      const anim = el.animate(
        [
          { transform: fromTransform, transformOrigin: origin },
          { transform: toTransform, transformOrigin: origin },
        ],
        { duration: 350, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' },
      );
      anim.onfinish = () => {
        el.style.overflow = '';
      };
    });
  }, [viewMode, activeTaskId]);

  /* ══════════════════════════════════════════════════════════
     PINCH GESTURE — two-finger pinch on the content area
     ══════════════════════════════════════════════════════════ */
  useEffect(() => {
    const el = contentRef.current;
    if (!el || !onToggleViewMode) return;

    let initialDist = 0;
    let fired = false;

    function dist(t1: Touch, t2: Touch) {
      const dx = t1.clientX - t2.clientX;
      const dy = t1.clientY - t2.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    function onStart(e: TouchEvent) {
      if (e.touches.length === 2) {
        initialDist = dist(e.touches[0], e.touches[1]);
        fired = false;
      }
    }

    function onMove(e: TouchEvent) {
      if (e.touches.length !== 2 || initialDist === 0) return;
      // Always prevent native pinch-zoom while two fingers are down
      e.preventDefault();
      if (fired) return;
      const d = dist(e.touches[0], e.touches[1]);
      const ratio = d / initialDist;

      if (ratio < 0.6 && viewMode === 'carousel') {
        fired = true;
        AUDIO.tap();
        captureRects();
        onToggleViewMode();
      } else if (ratio > 1.6 && viewMode === 'grid') {
        fired = true;
        AUDIO.tap();
        captureRects();
        onToggleViewMode();
      }
    }

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
    };
  }, [viewMode, onToggleViewMode, captureRects]);

  /* IntersectionObserver — detect which card is centered (carousel only) */
  useEffect(() => {
    if (viewMode !== 'carousel') return;
    const carousel = carouselRef.current;
    if (!carousel || incompleteTasks.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry with the highest intersection ratio
        let best: IntersectionObserverEntry | null = null;
        for (const entry of entries) {
          if (!best || entry.intersectionRatio > best.intersectionRatio) {
            best = entry;
          }
        }
        if (best && best.intersectionRatio > 0.5) {
          const taskId = (best.target as HTMLElement).dataset.taskId;
          if (taskId) setVisibleTaskId(taskId);
        }
      },
      { root: carousel, threshold: [0.5, 0.75, 1.0] },
    );

    itemRefs.current.forEach(el => {
      if (el.closest('.tl-carousel')) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [incompleteTasks.length, viewMode]);

  /* Programmatic scroll when activeTaskId changes (e.g. from TaskNavigator).
     Skip when triggered by a grid→carousel mode switch — the FLIP useLayoutEffect
     already positions the card instantly via scrollLeft. */
  const modeSwitchedRef = useRef(false);
  const prevModeForScrollRef = useRef(viewMode);
  useEffect(() => {
    if (prevModeForScrollRef.current !== viewMode) {
      modeSwitchedRef.current = true;
      prevModeForScrollRef.current = viewMode;
    }
  }, [viewMode]);

  useEffect(() => {
    if (!activeTaskId || viewMode !== 'carousel') return;
    if (modeSwitchedRef.current) { modeSwitchedRef.current = false; return; }
    const container = carouselRef.current;
    const el = itemRefs.current.get(activeTaskId);
    if (container && el) {
      // Instant snap — no visible scroll animation
      container.style.scrollSnapType = 'none';
      container.style.scrollBehavior = 'auto';
      container.scrollLeft = el.offsetLeft - (container.clientWidth - el.clientWidth) / 2;
      setVisibleTaskId(activeTaskId);
      requestAnimationFrame(() => {
        setTimeout(() => {
          container.style.scrollSnapType = '';
          container.style.scrollBehavior = '';
        }, 50);
      });
    }
  }, [activeTaskId, viewMode]);

  /* ── Rubber-band overscroll on carousel edges ── */
  useRubberBand(carouselRef, viewMode === 'carousel', 'x');

  /* Dots: show all tasks — completed + incomplete. Active follows carousel. */
  const dots = tasks.map(t => {
    const s = getState(t);
    if (s === 'complete' || s === 'just-completed') return 'done' as const;
    if (t.id === centeredTask?.id) return 'active' as const;
    if (s === 'urgent') return 'urgent' as const;
    return 'default' as const;
  });




  const setItemRef = useCallback((taskId: string) => (el: HTMLDivElement | null) => {
    if (el) itemRefs.current.set(taskId, el);
    else itemRefs.current.delete(taskId);
  }, []);

  /* Grid card tap — completed tasks expand submissions drawer, others expand to carousel */
  const handleGridCardTap = (task: Task) => {
    const s = getState(task);
    if (s === 'complete' || s === 'just-completed') {
      // Switch back to carousel and expand the submissions drawer
      captureRects();
      onToggleViewMode?.();
      setFeedExpanded(true);
    } else {
      captureRects();
      setVisibleTaskId(task.id);
      onSelectTask?.(task);
      onToggleViewMode?.();
    }
  };

  return (
    <div className="tl-container">
      {/* Story dots */}
      <div className="tl-dots">
        {dots.map((d, i) => (
          <div
            key={i}
            className={`tl-dot ${d === 'done' ? 'tl-dot-done' : d === 'active' ? 'tl-dot-active' : d === 'urgent' ? 'tl-dot-urgent' : ''}`}
          />
        ))}
      </div>

      {/* Scrollable content */}
      <div className="tl-content" ref={contentRef}>
        {viewMode === 'carousel' ? (
          <>
            {/* Glass theme uses a rotatable card stack instead of scroll-snap carousel */}
            {incompleteTasks.length > 0 && (() => {
              const isGlass = typeof document !== 'undefined' &&
                document.documentElement.getAttribute('data-theme') === 'glass';

              const renderTaskCard = (task: Task, isFocused: boolean) => (
                <TaskCard
                  task={task}
                  state={getState(task)}
                  isFocused={isFocused}
                  timeLeft={formatTimeLeft(task.dueDate)}
                  onStart={() => onStartTask?.(task)}
                  onResume={() => onResumeTask?.(task)}
                  resuming={resumingTaskId === task.id}
                  onNext={() => {
                    const idx = incompleteTasks.findIndex(t => t.id === task.id);
                    const next = incompleteTasks[idx + 1] || incompleteTasks[0];
                    if (next && next.id !== task.id) {
                      const el = itemRefs.current.get(next.id);
                      if (el) el.scrollIntoView({ behavior: 'smooth', inline: 'center' });
                    }
                  }}
                  onViewAll={undefined}
                  onSequenceDone={getState(task) === 'just-completed' ? onSubmitSequenceDone : undefined}
                  milestone={getState(task) === 'just-completed' ? milestone : undefined}
                  submittedAt={getState(task) === 'just-completed' ? submittedAt : undefined}
                  onRedo={getState(task) === 'needs_redo' ? () => onRedoTask?.(task) : undefined}
                  redoReason={getEntry(task.id)?.redoReason}
                />
              );

              return isGlass ? (() => {
                // Glass deck: exclude both 'complete' and 'just-completed' so a
                // card vanishes from the deck the moment its upload finishes.
                // 'pending' (upload-in-progress) stays in the deck so the user
                // sees the inactive uploading card until it completes.
                const stackTasks = incompleteTasks.filter(t => getState(t) !== 'just-completed');
                const stackActiveId = (
                  (visibleTaskId && stackTasks.some(t => t.id === visibleTaskId) && visibleTaskId) ||
                  (activeTaskId && stackTasks.some(t => t.id === activeTaskId) && activeTaskId) ||
                  stackTasks[0]?.id
                );
                return (
                  <GlassCardStack
                    tasks={stackTasks}
                    renderCard={(task, _idx, isActive) => renderTaskCard(task, isActive)}
                    activeTaskId={stackActiveId}
                    onActiveTaskChange={setVisibleTaskId}
                    setItemRef={setItemRef}
                  />
                );
              })() : (
                <div className="tl-carousel" ref={carouselRef}>
                  {incompleteTasks.map(task => (
                    <div
                      key={task.id}
                      className="tl-carousel-item"
                      data-task-id={task.id}
                      ref={setItemRef(task.id)}
                    >
                      {renderTaskCard(task, task.id === centeredTask?.id)}
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Sticky submissions section — bar + drawer stick to top on scroll */}
            <div className="sab-sticky">
              <SmartActionBar
                completedCount={completedTasks.length}
                totalCount={tasks.length}
                submissionCount={submissions.length + pendingJobs.length}
                unreadMessageCount={unreadMessageCount}
                redoCount={redoCount}
                expanded={feedExpanded}
                onToggle={() => { AUDIO.tap(); setFeedExpanded(e => !e); }}
                onViewMessages={onViewMessages}
              />
            </div>

            {/* Submissions drawer — expands inline below the sticky bar */}
            <div className={`sab-drawer ${feedExpanded ? 'sab-drawer--open' : ''}`}>
              <div className="sab-drawer-inner">
                {feedExpanded && (
                  <div className="sab-drawer-scroll">
                    {pendingJobs.map(job => {
                      const t = taskMap.get(job.taskId);
                      const statusLabel = { queued: 'Waiting\u2026', compressing: 'Compressing\u2026', uploading: 'Uploading\u2026', finalizing: 'Saving\u2026' }[job.status] || 'Uploading\u2026';
                      const showProg = job.status === 'uploading' || job.status === 'compressing';
                      return (
                        <div key={`pending-${job.id}`} className="sc-card sc-pending-card">
                          <div className="sc-media sc-pending-media">
                            {job.thumbnailUrl ? (
                              <img className="sc-pending-thumb" src={job.thumbnailUrl} alt="" />
                            ) : (
                              <div className="sc-media-placeholder sc-pending-placeholder">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(0,0,0,0.2)" strokeWidth="1.5" strokeLinecap="round">
                                  <path d="M21 12a9 9 0 1 1-6.2-8.56" />
                                </svg>
                              </div>
                            )}
                            {showProg && (
                              <div className="sc-pending-progress-track">
                                <div className="sc-pending-progress-fill" style={{ width: `${job.progress}%` }} />
                              </div>
                            )}
                          </div>
                          <div className="sc-body">
                            <div className="sc-task-label">{t?.title || t?.prompt || job.taskTitle}</div>
                            <div className="sc-meta">
                              <span className="sc-pending-status">{statusLabel} {showProg ? `${job.progress}%` : ''}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {sortedSubmissions.length === 0 && pendingJobs.length === 0 ? (
                      <div className="sab-drawer-empty">No submissions yet</div>
                    ) : (
                      (() => {
                        // Group: originals first, redo children nested under their parent
                        const originals = sortedSubmissions.filter(s => !s.parentSubmissionId || s.submissionType !== 'redo');
                        const redosByParent = new Map<string, typeof sortedSubmissions>();
                        for (const s of sortedSubmissions) {
                          if (s.parentSubmissionId && s.submissionType === 'redo') {
                            const arr = redosByParent.get(s.parentSubmissionId) || [];
                            arr.push(s);
                            redosByParent.set(s.parentSubmissionId, arr);
                          }
                        }

                        return originals.map(sub => {
                          const task = taskMap.get(sub.taskId || '');
                          const addendumTime = getAddendumTimeLeft(sub, task);
                          const redoChildren = redosByParent.get(sub.id) || [];
                          const hasNewerRedo = redoChildren.length > 0;
                          const redoReason = sub.redoRequestedBy
                            ? (taskStates.find(e => e.taskId === sub.taskId)?.redoReason || 'Please redo this task')
                            : undefined;

                          return (
                            <div key={sub.id} data-task-id={sub.taskId} data-submission-id={sub.id}>
                              <SubmissionCard
                                submission={sub}
                                task={task}
                                threadMessages={threadsBySubmission?.get(sub.id)}
                                unreadCount={unreadCounts?.get(sub.id)}
                                threadContext={threadContext}
                                onAddendum={addendumTime && task && onAddendum ? () => onAddendum(sub, task) : undefined}
                                addendumTimeLeft={addendumTime || undefined}
                                onRedo={sub.redoRequestedAt && !hasNewerRedo && task && onRedoTask ? () => onRedoTask(task) : undefined}
                                redoReason={redoReason}
                                hasNewerRedo={hasNewerRedo}
                              />
                              {/* Redo children — nested under parent */}
                              {redoChildren.map(redo => {
                                const redoAddendumTime = getAddendumTimeLeft(redo, task);
                                return (
                                  <div key={redo.id} data-task-id={redo.taskId} data-submission-id={redo.id}>
                                    <SubmissionCard
                                      submission={redo}
                                      task={task}
                                      threadMessages={threadsBySubmission?.get(redo.id)}
                                      unreadCount={unreadCounts?.get(redo.id)}
                                      threadContext={threadContext}
                                      onAddendum={redoAddendumTime && task && onAddendum ? () => onAddendum(redo, task) : undefined}
                                      addendumTimeLeft={redoAddendumTime || undefined}
                                      isRedoChild
                                      attemptNumber={redo.attemptNumber}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          );
                        });
                      })()
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          /* ── Grid view — all tasks in 2-up compact cards ── */
          <div className="tl-grid">
            {gridTasks.map(task => (
              <div
                key={task.id}
                className="tl-grid-card"
                data-task-id={task.id}
                ref={setItemRef(task.id)}
              >
                <MiniTaskCard
                  task={task}
                  state={getState(task)}
                  timeLeft={formatTimeLeft(task.dueDate)}
                  overdue={!!task.dueDate && new Date(task.dueDate).getTime() <= Date.now()}
                  onClick={() => handleGridCardTap(task)}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Help overlay */}
      {showHelp && <HelpOverlay userId={helpUserId} onClose={() => setShowHelp(false)} />}

    </div>
  );
});
